import { createContext, createElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AgingReport } from "common";
import { useUser } from "../store";
import { ClaimAlert, compareNewestFirst, getReportAlerts } from "../util/alerts";
import { REPORTS_CHANGED_EVENT, REPORTS_STALE_EVENT, whenPageShown } from "../util/reportEvents";

const REFRESH_MS = 30 * 1000;

interface PayerAlertReports {
  facility: string;
  payer: string;
  revision: string;
  /** Only the reports that have messages; absent when the revision we sent is still current */
  reports?: AgingReport[];
}

/** Overdue follow-ups and messages across every facility and payer of the signed-in user */
const useAlertsLoader = (enabled: boolean) => {
  const { invokeApi, user, group } = useUser();
  // invokeApi is a new function on every navigation; depending on it would reload every payer each time a tab is clicked
  const invokeApiRef = useRef(invokeApi);
  invokeApiRef.current = invokeApi;
  const username: string = user?.username;
  const isAdmin = user?.type === "Admin";
  const [reports, setReports] = useState<Record<string, AgingReport[]>>({});
  // Bumped on every refresh so alerts that depend on today's date are recomputed even when the data is unchanged
  const [tick, setTick] = useState(0);
  // True once the server has answered once, so the first load is not mistaken for new arrivals
  const [loaded, setLoaded] = useState(false);
  const revisions = useRef<Record<string, string | null>>({});
  const loadRef = useRef<() => void>(() => undefined);

  // Stable text so a profile refresh with the same permissions does not refetch everything
  const targetsKey = JSON.stringify({ facilities: user?.facilities ?? [], facilitiesPayers: user?.facilitiesPayers ?? {} });

  useEffect(() => {
    if (!enabled || !username || !group) return;
    revisions.current = {};
    setReports({});
    let cancelled = false;
    let inFlight = false;

    // One request covers every payer the user can see; the server leaves out those whose revision we already hold
    const load = () => {
      if (document.visibilityState === "hidden" || inFlight) return;
      inFlight = true;
      invokeApiRef.current({ endpoint: "/getAlertReports", data: { revisions: { ...revisions.current } }, silent: true })
        .then((response: { payers?: PayerAlertReports[] }) => {
          if (cancelled || !response?.payers) return;
          setTick((value) => value + 1);
          const changed: Record<string, AgingReport[]> = {};
          response.payers.forEach(({ facility, payer, revision, reports: list }) => {
            const key = `${facility}#${payer}`;
            revisions.current[key] = revision;
            if (list) changed[key] = list;
          });
          if (Object.keys(changed).length) setReports((previous) => ({ ...previous, ...changed }));
        })
        .catch(() => {
          // Alerts are best-effort; the dashboards report real failures
        })
        .finally(() => {
          inFlight = false;
          if (!cancelled) setLoaded(true);
        });
    };
    loadRef.current = load;

    load();
    const timer = setInterval(load, REFRESH_MS);
    const stopWatchingPage = whenPageShown(load);
    window.addEventListener(REPORTS_CHANGED_EVENT, load);
    window.addEventListener(REPORTS_STALE_EVENT, load);
    return () => {
      cancelled = true;
      clearInterval(timer);
      stopWatchingPage();
      window.removeEventListener(REPORTS_CHANGED_EVENT, load);
      window.removeEventListener(REPORTS_STALE_EVENT, load);
    };
  }, [enabled, username, group, targetsKey]);

  const refresh = useCallback(() => loadRef.current(), []);

  const alerts = useMemo<ClaimAlert[]>(
    () =>
      Object.entries(reports)
        .flatMap(([key, list]) => {
          const [facility, payer] = key.split("#");
          return list.flatMap((report) => getReportAlerts(report, { username, isAdmin, facility, payer }));
        })
        .sort(compareNewestFirst),
    // tick only forces a recompute, because the alerts depend on today's date
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reports, username, isAdmin, tick]
  );

  return { alerts, refresh, loaded };
};

type AlertsState = ReturnType<typeof useAlertsLoader>;
const AlertsContext = createContext<AlertsState | null>(null);

/** Loads the alerts once for the whole app, so the navbar and the dashboard share one set of requests */
export const AlertsProvider = ({ children }: { children: ReactNode }) =>
  createElement(AlertsContext.Provider, { value: useAlertsLoader(true) }, children);

export const useAlerts = (): AlertsState => {
  const shared = useContext(AlertsContext);
  // Without a provider (tests, isolated pages) the hook loads on its own
  const own = useAlertsLoader(!shared);
  return shared ?? own;
};
